import Const from 'const';

interface SubGroupStruct {
    readonly teamName: string;
    readonly subGroupKey: string;
    readonly subGroupNumber: number;
    readonly isDefensemanIncluded: boolean;
}

class SubGroup {
    constructor(private readonly data: SubGroupStruct) {}

    public isPlayerFromSubGroup(player): boolean {
        if (player.isGoalie()) {
            return false;
        }

        if (player.team !== this.data.teamName) {
            return false;
        }

        if (player.isDefenseman() && !this.data.isDefensemanIncluded) {
            return false;
        }

        if (this.data.subGroupKey === 'LG') {
            return Number(player.userLineGroup) === this.data.subGroupNumber;
        } else if (this.data.subGroupKey === 'PL') {
            return Number(player.userPpLine) === this.data.subGroupNumber;
        }

        return false;
    }
}

export class LineupStackExtractor {
    private readonly markedItems: string[] = [];
    private readonly stackedSubGroups: SubGroup[] = [];

    constructor(
        private readonly lineup: string[],
        private readonly teamStacking,
        private readonly marker,
    ) {
        this.markedItems = this.marker.pickMarkedBySuffix(this.lineup);

        this.extractStackedSubGroups();
    }

    public isPlayerFromFirstStack(player): boolean {
        return this.isPlayerFromStack(player, Const.NHL_TEAM_STACKING_FIRST_STACK);
    }

    public isPlayerFromSecondStack(player): boolean {
        return this.isPlayerFromStack(player, Const.NHL_TEAM_STACKING_SECOND_STACK);
    }

    public isPlayerFromThirdStack(player): boolean {
        return this.isPlayerFromStack(player, Const.NHL_TEAM_STACKING_THIRD_STACK);
    }

    private isPlayerFromStack(player, stackNumber: number): boolean {
        const key = stackNumber - 1;

        return key in this.stackedSubGroups ? this.stackedSubGroups[key].isPlayerFromSubGroup(player) : false;
    }

    private extractStackedSubGroups(): void {
        const stackedSubGroups: {[key: string]: SubGroup[]} = {};

        this.markedItems.map((markedItem) => {
            if (markedItem[0] !== Const.LP_SUBGROUP_PREFIX) {
                return;
            }

            const [teamName, comboId, stackNumber, subGroupKey, subGroupNumber] = markedItem
                .replace(Const.LP_SUBGROUP_PREFIX, '')
                .split(Const.LP_ITEM_DELIMITER);

            const combo = this.teamStacking.getCombination(comboId);

            if (combo === null) {
                return;
            }

            const subGroup = new SubGroup({
                teamName,
                subGroupKey,
                subGroupNumber: Number(subGroupNumber),
                isDefensemanIncluded: this.isDefensemanIncluded(combo, stackNumber, teamName),
            });

            stackedSubGroups[stackNumber] ??= [];
            stackedSubGroups[stackNumber].push(subGroup);
        });

        Object.values(stackedSubGroups).map((subGroups) => {
            subGroups.map((subGroup) => this.stackedSubGroups.push(subGroup));
        });
    }

    private isDefensemanIncluded(combo, stackNumber, teamName): boolean {
        return (
            this.teamStacking.isDefensemanMandatoryInCombinationStack(combo, stackNumber) ||
            this.teamStacking.isTeamDefensemanIncluded(teamName)
        );
    }
}
